import { useLiveQuery } from "dexie-react-hooks";
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { todayIn } from "../../shared/dates";
import { ApiError, api, type Me } from "./api";
import { db, getMeta, setMeta, wipeLocalData, type LocalContract } from "./db";
import { SyncEngine, type SyncStatus } from "./sync";

interface Session {
  me: Me | null;
  loading: boolean;
  /** Set when /api/me failed for a reason other than "signed out" (offline, server down). */
  offlineMe: boolean;
  engine: SyncEngine;
  sync: SyncStatus;
  today: string;
  refreshMe: () => Promise<void>;
  signOut: (everywhere?: boolean) => Promise<void>;
}

const SessionContext = createContext<Session | null>(null);

let tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
export const engine = new SyncEngine(db, api, () => tz);

export function SessionProvider({ children }: { children: ReactNode }) {
  const [me, setMe] = useState<Me | null>(null);
  const [loading, setLoading] = useState(true);
  const [offlineMe, setOfflineMe] = useState(false);
  const [sync, setSync] = useState<SyncStatus>(engine.status);
  // Background syncs (focus, reconnect, timer) only make sense with a user; signed out they'd all be 401s.
  const signedIn = useRef(false);

  const refreshMe = useCallback(async () => {
    try {
      const fresh = await api<Me>("/api/me");
      // A different person signed in on this device: never show them the previous user's cache.
      const previous = await getMeta<string | null>(db, "userId", null);
      if (previous && previous !== fresh.user.id) await wipeLocalData();
      await setMeta(db, "userId", fresh.user.id);
      await setMeta(db, "me", fresh);
      tz = fresh.org.timezone;
      setMe(fresh);
      setOfflineMe(false);
      signedIn.current = true;
      void engine.sync();
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        signedIn.current = false;
        setMe(null);
      } else {
        // Offline: use the last known profile so the cached contracts stay usable,
        // and keep trying to sync so changes go out once the connection is back.
        const cached = await getMeta<Me | null>(db, "me", null);
        signedIn.current = cached !== null;
        setMe(cached);
        setOfflineMe(true);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refreshMe();
    const unsub = engine.subscribe(setSync);
    const online = () => signedIn.current && void engine.sync();
    const visible = () => signedIn.current && document.visibilityState === "visible" && void engine.sync();
    window.addEventListener("online", online);
    document.addEventListener("visibilitychange", visible);
    const timer = setInterval(() => signedIn.current && navigator.onLine && document.visibilityState === "visible" && void engine.sync(), 60_000);
    return () => {
      unsub();
      window.removeEventListener("online", online);
      document.removeEventListener("visibilitychange", visible);
      clearInterval(timer);
    };
  }, [refreshMe]);

  useEffect(() => {
    if (sync.state === "signed_out") {
      signedIn.current = false;
      setMe(null);
    }
  }, [sync.state]);

  const signOut = useCallback(async (everywhere = false) => {
    await api(everywhere ? "/api/auth/logout-all" : "/api/auth/logout", { method: "POST" }).catch(() => {});
    signedIn.current = false;
    await wipeLocalData();
    setMe(null);
  }, []);

  const value = useMemo<Session>(
    () => ({ me, loading, offlineMe, engine, sync, today: todayIn(me?.org.timezone ?? tz), refreshMe, signOut }),
    [me, loading, offlineMe, sync, refreshMe, signOut],
  );
  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): Session {
  const s = useContext(SessionContext);
  if (!s) throw new Error("useSession outside SessionProvider");
  return s;
}

/** Live list of non-deleted contracts from IndexedDB. Undefined while loading. */
export function useContracts(): LocalContract[] | undefined {
  return useLiveQuery(async () => (await db.contracts.toArray()).filter((c) => !c.deletedAt), []);
}

export function useContract(id: string | undefined): LocalContract | null | undefined {
  return useLiveQuery(async () => (id ? ((await db.contracts.get(id)) ?? null) : null), [id]);
}

export function useConflictNotes() {
  return useLiveQuery(() => db.conflicts.orderBy("id").reverse().toArray(), []) ?? [];
}

export function useOnline(): boolean {
  const [online, setOnline] = useState(typeof navigator === "undefined" ? true : navigator.onLine);
  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => {
      window.removeEventListener("online", on);
      window.removeEventListener("offline", off);
    };
  }, []);
  return online;
}
